import { makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@material-ui/core';
import Avatar from 'components/shared/members/Avatar';
import { compareNumbers, compareString } from 'helpers/compareFunctions';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useGetCurrentUserQuery } from 'utils/wemble-api';
import { PublicUserDto, UserUtilizationEntry } from 'utils/wemble-api.generated';
import { getStatusFromUser, statusColor, statusTitle } from './Forecast/utils';
import { titleRank } from './DetailedTable';
import VacationPlanner from 'components/vacationPlanner/VacationPlanner';


function colorForWorkload(workload: number | undefined | null) {
    return statusColor(Math.floor(Math.round((workload || 0) * 10) / 10 + 0.5).toString())
}

const useStyles = makeStyles((theme) => ({
    root: {
        fontFamily: 'Roboto',
    },
    utilization__content: {
        backgroundColor: 'white',
        padding: '20px 16px',
        [theme.breakpoints.down('sm')]: {
            padding: 12
        },
        borderRadius: 16,
        display: 'flex',
        flexDirection: 'column',
    },
    table: {

        '& .MuiTableCell-root': {
            borderBottomColor: theme.palette.gray.g6,
            padding: '6px 16px !important',
            [theme.breakpoints.down('lg')]: {
                padding: '6px 10px !important',

            },
            [theme.breakpoints.down('md')]: {
                padding: '6px 6px !important',

            },
            [theme.breakpoints.down('sm')]: {
                padding: '6px 4px !important',

            },
        },
        '& .MuiTableCell-head': {
            [theme.breakpoints.down('md')]: {
                fontSize: 11,

            },
            fontSize: 12,
            textTransform: 'uppercase',
            color: theme.palette.gray.g10,

        },
        '& .MuiTableCell-body': {
            fontWeight: 500,
        },
        '& .MuiTableBody-root .MuiTableRow-root:nth-last-child(1) > td': {
            border: 'none',
        },
    },
}));

const formatWeekRange = (weekStart: string): string => {
    const start = moment(weekStart);
    const end = start.clone().add(6, 'days');
    if (start.month() === end.month()) {
        return `${start.locale('en').format('MMM D')}-${end.format('D')}`;
    }
    return `${start.locale('en').format('MMM D')}-${end.locale('en').format('MMM D')}`;
};

const useSortFunction = (weekLabels: string[], users: PublicUserDto[] | undefined) => {
    const { data: currentUser } = useGetCurrentUserQuery();

    return (sortMethod: string) => (a: UserUtilizationEntry, b: UserUtilizationEntry) => {
        const getCurrentWorkload = (userId: string) => {
            const status = getStatusFromUser(users?.find(u => u._id === userId));
            if (status == undefined) return 5;
            if (status == 'away') return 4;
            return parseInt(status);
        };

        const getWeekIndex = (method: string) => {
            const label = method.endsWith(' B') ? method.replace(' B', '') : method;
            return weekLabels.indexOf(label);
        };

        switch (sortMethod) {
            case 'NAME':
                return compareString(currentUser?.name == a.displayName ? 'AAA' : a.displayName, currentUser?.name == b.displayName ? 'AAA' : b.displayName);
            case 'TITLE':
                const rankA = titleRank(currentUser?.company, a.title);
                const rankB = titleRank(currentUser?.company, b.title);
                if (rankA === 0 && rankB === 0) return compareString(a.title || 'zzz', b.title || 'zzz');
                return compareNumbers(rankB, rankA);
            case 'AVERAGE':
                return compareNumbers(b.weeklyAverage, a.weeklyAverage);
            case 'UTILIZATION':
                return compareNumbers(b.utilizationAverage, a.utilizationAverage);
            case 'CURRENT STATUS':
                return compareNumbers(getCurrentWorkload(a.userId), getCurrentWorkload(b.userId));
            case 'AVERAGE STATUS':
                return compareNumbers(b.avgWorkload ?? -1, a.avgWorkload ?? -1);
            default: {
                // Sort by utilization override month (e.g. "UTILIZATION March")
                if (sortMethod.startsWith('UTILIZATION ')) {
                    const month = sortMethod.replace('UTILIZATION ', '');
                    const getRate = (u: UserUtilizationEntry) => {
                        const override = u.utilizationOverrides?.find(o => moment(o.periodStart).locale('en').format('MMMM') === month);
                        return override?.utilizationRate ?? -1;
                    };
                    return compareNumbers(getRate(b), getRate(a));
                }
                // Sort by week column
                const weekIndex = getWeekIndex(sortMethod);
                if (weekIndex >= 0) {
                    const weekA = a.weeklyOverview?.[weekIndex];
                    const weekB = b.weeklyOverview?.[weekIndex];
                    if (sortMethod.endsWith(' B')) {
                        return compareNumbers(weekB?.nonBillableHours || 0, weekA?.nonBillableHours || 0);
                    }
                    return compareNumbers(weekB?.billableHours || 0, weekA?.billableHours || 0);
                }
                return 0;
            }
        }
    };
};

const UtilizationTable = ({ utilizationData, filteredUsers }: { utilizationData: UserUtilizationEntry[], filteredUsers?: PublicUserDto[] }) => {
    const classes = useStyles();
    const theme = useTheme();
    const [sortMethod, setSortMethod] = useState<string>(localStorage.getItem("UtilizationPageSortMethod") || 'NAME');
    const [vacationPlanner, setVacationPlanner] = useState<string | undefined>(undefined);

    useEffect(() => {
        localStorage.setItem("UtilizationPageSortMethod", sortMethod)
    }, [sortMethod])

    // Build week labels from the first user that has data
    const weekLabels = utilizationData[0]?.weeklyOverview?.map(w => formatWeekRange(w.weekStart)) || [];

    // Check if any user has utilization overrides (e.g. HS monthly objectives)
    const hasOverrides = utilizationData.some(u => u.utilizationOverrides && u.utilizationOverrides.length > 0);

    // Build override period labels sorted chronologically
    const overrideLabels = hasOverrides
        ? Array.from(new Set(
            utilizationData.flatMap(u => u.utilizationOverrides || [])
                .sort((a, b) => a.periodStart.localeCompare(b.periodStart))
                .map(o => moment(o.periodStart).locale('en').format('MMMM'))
          ))
        : [];

    const sortFunction = useSortFunction(weekLabels, filteredUsers);
    const columns = [
        "CURRENT STATUS",
        ...weekLabels,
        ...(hasOverrides
            ? overrideLabels.map(m => `UTILIZATION ${m}`)
            : ['AVERAGE', 'UTILIZATION']),
        'AVERAGE STATUS',
    ];

    return (
        <div className={classes.utilization__content}>
            <TableContainer>
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <span onClick={() => setSortMethod('NAME')} style={{ color: sortMethod === 'NAME' ? '#4e505d' : '#7E8299', cursor: 'pointer' }}>NAME</span>
                                &nbsp;/&nbsp;
                                <span onClick={() => setSortMethod('TITLE')} style={{ color: sortMethod === 'TITLE' ? '#4e505d' : '#7E8299', cursor: 'pointer' }}>TITLE</span>
                            </TableCell>
                            {columns.map((column, i) => {
                                const isWeekColumn = i >= 1 && i <= weekLabels.length;
                                const isCurrentStatus = column === 'CURRENT STATUS';
                                return (
                                    <TableCell
                                        align={(isWeekColumn || isCurrentStatus) ? 'left' : 'right'}
                                        key={column}
                                        onClick={() => setSortMethod((sortMethod == column && isWeekColumn) ? (column + " B") : column)}
                                        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        <span style={{ color: sortMethod === column ? '#4e505d' : '#7E8299' }}>{column}</span>
                                        {isWeekColumn && <span onClick={(e) => { e.stopPropagation(); setSortMethod(column + " B"); }} style={{ color: sortMethod === (column + " B") ? '#4e505d' : '#7E8299', fontSize: 9, marginLeft: 6, cursor: 'pointer' }}>NB</span>}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...utilizationData].sort(sortFunction(sortMethod)).map((entry) => (
                            <TableRow key={entry.userId}>
                                <TableCell>
                                    <Avatar size={40} image={entry.profilePicture} name={entry.displayName} email={entry.title} />
                                </TableCell>
                                <TableCell>
                                    <span style={{ color: statusColor(getStatusFromUser(filteredUsers?.find(u => u._id === entry.userId))), fontWeight: 800 }}>{statusTitle(getStatusFromUser(filteredUsers?.find(u => u._id === entry.userId)))}</span>
                                </TableCell>
                                {entry.weeklyOverview?.map(week => (
                                    <TableCell key={week.weekStart} align='left'>
                                        {week.billableHours.toFixed(1)}&nbsp;
                                        <span style={{ color: theme.palette.gray.g10, fontSize: 11 }}>{week.nonBillableHours.toFixed(1)}</span>
                                    </TableCell>
                                ))}
                                {hasOverrides ? (
                                    overrideLabels.map(monthLabel => {
                                        const override = entry.utilizationOverrides?.find(o => moment(o.periodStart).locale('en').format('MMMM') === monthLabel);
                                        return (
                                            <TableCell key={monthLabel} align='right'>
                                                {override ? `${Math.round(override.utilizationRate * 100)}%` : '0%'}
                                            </TableCell>
                                        );
                                    })
                                ) : (
                                    <>
                                        <TableCell align='right'>{entry.weeklyAverage.toFixed(1)}</TableCell>
                                        <TableCell align='right'>{Math.round(entry.utilizationAverage * 100)}%</TableCell>
                                    </>
                                )}
                                <TableCell align='right'>
                                    {typeof entry.avgWorkload === 'number' && (
                                        <span style={{ color: colorForWorkload(entry.avgWorkload), fontWeight: 800 }}>{(entry.avgWorkload + 1).toFixed(1)}</span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredUsers && (<VacationPlanner
                isOpen={Boolean(vacationPlanner)}
                close={() => setVacationPlanner(undefined)}
                selectedUser={vacationPlanner}
                users={filteredUsers} />)}
        </div>
    );
};

export default UtilizationTable;
